//NAMESPACE TYPES
import { CurrencyPricingDtoAttributes } from "./currencypricing/CurrencyPricing"
import { PricingBracketRequestDto } from "./matrix/MatrixDto"

export namespace PRODUCT_TYPE {
    export const SINGLE = "single"
    export const ASSEMBLY = "assembly"
    export const MATRIX_PARENT = "matrix-parent"
    export const MATRIX_ITEM = "matrix-item"
    export const QUICK_PRODUCT = "quick-product"
    export const QUICK_PRODUCTS = "quick-products"
    export const BUNDLE = "bundle"
}
export type PRODUCT_TYPE = typeof PRODUCT_TYPE[keyof typeof PRODUCT_TYPE]

export namespace PRICING_STRATEGY {
    export const MULTIPLIER = "multiplier"
    export const BRACKET = "bracket"
    export const CUSTOM_BRACKET = "bracket-custom"
    export const BULK = "bulk"
    export const CUSTOM_FORMULA = "custom-formula"
}
export type PRICING_STRATEGY = typeof PRICING_STRATEGY[keyof typeof PRICING_STRATEGY]

export namespace BILLING_METHOD {
    export const ONE_TIME = "one-time"
    export const MONTHLY = "monthly"
    export const YEARLY = "yearly"
}
export type BILLING_METHOD = typeof BILLING_METHOD[keyof typeof BILLING_METHOD]

export namespace PRODUCT_UPDATE_OPTION {
    export const TITLE = "title"
    export const PRICING_STRATEGY = "pricingStrategy"
    export const BILLING_METHOD = "billingMethods"
    export const DEFAULT_UNIT_PRICE = "defaultUnitPrice"
    export const SHORT_DESCRIPTION = "shortDescription"
    export const LONG_DESCRIPTION = "longDescription"
    export const LABELIDS = "labelIds"
    export const SKU = "sku"
    export const BARCODE = "barcode"
}

const UPDATE_OPTION = [PRODUCT_UPDATE_OPTION.TITLE, PRODUCT_UPDATE_OPTION.SHORT_DESCRIPTION,
    PRODUCT_UPDATE_OPTION.LABELIDS, PRODUCT_UPDATE_OPTION.LONG_DESCRIPTION, PRODUCT_UPDATE_OPTION.SKU, PRODUCT_UPDATE_OPTION.BARCODE] as const
export const isProductUpdateOption = (x): x is PRODUCT_UPDATE_OPTION => UPDATE_OPTION.includes(x)
export type PRODUCT_UPDATE_OPTION = typeof PRODUCT_UPDATE_OPTION[keyof typeof PRODUCT_UPDATE_OPTION]

export namespace MATRIX_ITEM_UPDATE_OPTION {
    export const PRICE = "defaultUnitPrice"
    export const COST = "defaultUnitCost"
    export const MONTHLY_COST = "monthlyUnitCost"
    export const MONTHLY_PRICE = "monthlyUnitPrice"
    export const YEARLY_COST = "yearlyUnitCost"
    export const YEARLY_PRICE = "yearlyUnitPrice"
    export const PRICING_BRACKETS = "pricingBrackets"
    export const MULTI_CURRENCY_PRICING = "multiCurrencyPricing"
}
export type MATRIX_ITEM_UPDATE_OPTION = typeof MATRIX_ITEM_UPDATE_OPTION[keyof typeof MATRIX_ITEM_UPDATE_OPTION]

export namespace MATRIX_AXIS_TYPE {
    export const X = "x"
    export const Y = "y"
    export const Z = "z"
}
export type MATRIX_AXIS_TYPE = typeof MATRIX_AXIS_TYPE[keyof typeof MATRIX_AXIS_TYPE]

export namespace MATRIX_ITEM_STATUS {
    export const NEW = "new"
    export const UPDATED = "updated"
}
export type MATRIX_ITEM_STATUS = typeof MATRIX_ITEM_STATUS[keyof typeof MATRIX_ITEM_STATUS]


//REDUCER ACTION TYPES
export const PRODUCT_LOADED = "product-loaded"
export type PRODUCT_LOADED = typeof PRODUCT_LOADED

export const PRODUCT_CLEARED = "product-cleared"
export type PRODUCT_CLEARED = typeof PRODUCT_CLEARED

export const PRODUCT_UPDATED = "product-updated"
export type PRODUCT_UPDATED = typeof PRODUCT_UPDATED

export const MATRIX_AXIS_UPDATED = "matrix-axis-updated"
export type MATRIX_AXIS_UPDATED = typeof MATRIX_AXIS_UPDATED

export const MATRIX_AXIS_DELETED = "matrix-axis-deleted"
export type MATRIX_AXIS_DELETED = typeof MATRIX_AXIS_DELETED

export const MATRIX_ITEM_UPDATED = "matrix-item-updated"
export type MATRIX_ITEM_UPDATED = typeof MATRIX_ITEM_UPDATED

export const MATRIX_ITEM_DELETED = "matrix-item-deleted"
export type MATRIX_ITEM_DELETED = typeof MATRIX_ITEM_DELETED

export const MATRIX_ITEM_RESTORED = "matrix-item-restored"
export type MATRIX_ITEM_RESTORED = typeof MATRIX_ITEM_RESTORED

export const MODIFY_BUNDLE = "MODIFY_BUNDLE"
export type MODIFY_BUNDLE = typeof MODIFY_BUNDLE

//ACTION PAYLOAD TYPES
export type MatrixItemUpdatePayload = {
        updateType:
            | typeof MATRIX_ITEM_UPDATE_OPTION.PRICE
            | typeof MATRIX_ITEM_UPDATE_OPTION.COST
            | typeof MATRIX_ITEM_UPDATE_OPTION.MONTHLY_PRICE
            | typeof MATRIX_ITEM_UPDATE_OPTION.MONTHLY_COST
            | typeof MATRIX_ITEM_UPDATE_OPTION.YEARLY_PRICE
            | typeof MATRIX_ITEM_UPDATE_OPTION.YEARLY_COST,
        id: string, value: string
    }
    | { updateType: typeof MATRIX_ITEM_UPDATE_OPTION.PRICING_BRACKETS, id: string, value: PricingBracketRequestDto }
    | { updateType: typeof MATRIX_ITEM_UPDATE_OPTION.MULTI_CURRENCY_PRICING, id: string, value: CurrencyPricingDtoAttributes}

export type ProductUpdatePayload =
    | { updateType: typeof PRODUCT_UPDATE_OPTION.PRICING_STRATEGY, value: PRICING_STRATEGY }
    | { updateType: typeof PRODUCT_UPDATE_OPTION.TITLE, value: string }
    | { updateType: typeof PRODUCT_UPDATE_OPTION.BILLING_METHOD, value: BILLING_METHOD[] }
    | { updateType: typeof PRODUCT_UPDATE_OPTION.DEFAULT_UNIT_PRICE, value: string }
    | { updateType: PRODUCT_UPDATE_OPTION, value: string }

export type MatrixItemPayload = {
    id: string
}

export namespace UNIT_OF_MEASURE {
    export const PIECE = "piece"
    export const INCH = "inch"
    export const FOOT = "foot"
    export const YARD = "yard"
    export const MILE = "mile"
    export const CENTIMETER = "centimeter"
    export const METER = "meter"
    export const KILOMETER = "kilometer"
    export const SQUARE_METER = "square-meter"
    export const CUBIC_METER = "cubic-meter"
    export const LITER = "liter"
    export const MILLILITER = "milliliter"
    export const GRAM = "gram"
    export const KILOGRAM = "kilogram"
    export const TON = "ton"
    export const OUNCE = "ounce"
    export const POUND = "pound"
    export const GALLON = "gallon"
    export const MINUTE = "minute"
    export const HOUR = "hour"
    export const HOURS_MINUTE = "hours-minutes"
    export const DAY = "day"
    export const MONTH = "month"
    export const QUARTER = "quarter"
    export const YEAR = "year"
    export const TRAVEL = "travel"
    export const PARTICIPANT = "participant"
    export const USER = "user"
    export const PAGE = "page"
    export const PERSON = "person"
    export const GIGABYTE = "gigabyte"
    export const MEGABYTE = "megabyte"
    export const ASSET = "asset"
    export const EPISODE = "episode"
    export const TALENT = "talent"
    export const SQUARE_FOOT = "square-foot"
    export const SERVING = "serving"
}
export type UNIT_OF_MEASURE = typeof UNIT_OF_MEASURE[keyof typeof UNIT_OF_MEASURE]

export type ItemAdjustment = {
    productId: string
    currency: string
    measure: string
}

export type productQohAdjustment = {
    inventoryLocationId: string
    definitionId: string
    itemAdjustment: ItemAdjustment
}

export namespace EXPORT_TYPE {
    export const INVENTORY_LEVELS = "inventory-levels"
    export const PRODUCT = "product"
    export const SUPPLIER_COSTS = "supplier-costs"
}

export namespace IMPORT_FROM {
    export const INTEGRATION = "integration"
    export const CSV = "csv"
}


// WEBPACK FOOTER //
// ./src/state/product/Types.ts